/**
 * The view "Edit room" opens the room editor in: the floor plan view's own,
 * and in 3D the angle its camera looks from, turned into the room's frame,
 * since the room editor draws the room unturned. Both platforms' floor plan
 * views hand it to their host, and the room editor takes it as `defaultView`.
 */
import { type FloorPlanRoom, roomPlacementRadians } from '@kaiizen/kai-floor-plan';
import type { CameraAngle } from '../view3d';
import type { AssemblerMutableState } from './assemblerState';

/** The view the room editor opens in: 2D, or 3D looking from `angle` (null for its own 3/4 opening angle). */
export type OpeningView = { mode: '2d' } | { mode: '3d'; angle: CameraAngle | null };

/** `angle` as the room editor sees `room`: the camera turns with the room's placement. */
const angleInRoom = ({ azimuth, elevation }: CameraAngle, room: FloorPlanRoom): CameraAngle => ({ azimuth: azimuth + roomPlacementRadians(room), elevation });

/** The view to open room `id` in, given the angle the floor plan view's 3D camera looks from (null before 3D first shows). */
export function roomOpeningView({ mode, rooms }: AssemblerMutableState, id: string, camera: CameraAngle | null): OpeningView {
  if (mode !== '3d') return { mode: '2d' };
  const room = rooms.find(assemblyRoom => assemblyRoom.id === id)?.room;
  return { mode: '3d', angle: camera && room ? angleInRoom(camera, room) : null };
}
